import { useMemo } from 'react'
import { create } from 'zustand'
import { createJSONStorage, devtools, persist } from 'zustand/middleware'
import type { CalendarShowBy } from '@/routes/features/projects/components/calendar/calendarLayout'

export type CalendarColorBy = 'project' | 'status' | 'priority'
export type CalendarMode = 'month' | 'week'
/** Sunday or Monday; null follows the user's "week starts" preference. */
export type CalendarWeekStart = 0 | 1 | null

/**
 * Calendar display options (design 26b), remembered per view. "Show
 * completed" is not here: the Display popover's shared row (HideCompletedStore)
 * drives it for every layout.
 */
export interface CalendarDisplayPrefs {
  showBy: CalendarShowBy
  colorBy: CalendarColorBy
  weekStartsOn: CalendarWeekStart
  showWeekends: boolean
  unscheduledTray: boolean
  mode: CalendarMode
}

export const defaultCalendarDisplay: CalendarDisplayPrefs = {
  showBy: 'range',
  colorBy: 'project',
  weekStartsOn: null,
  showWeekends: true,
  unscheduledTray: true,
  mode: 'month',
}

interface CalendarDisplayState {
  /** viewKey (project / space id) -> the options that differ from the defaults. */
  prefs: Record<string, Partial<CalendarDisplayPrefs>>
  setPref: <TKey extends keyof CalendarDisplayPrefs>(
    viewKey: string,
    key: TKey,
    value: CalendarDisplayPrefs[TKey],
  ) => void
  /** Back to the defaults for this view only. Keeps the Week / Month choice. */
  reset: (viewKey: string) => void
}

const useCalendarDisplayStore = create<CalendarDisplayState>()(
  devtools(
    persist(
      (set) => ({
        prefs: {},
        setPref: (viewKey, key, value) =>
          set((state) => ({
            prefs: { ...state.prefs, [viewKey]: { ...state.prefs[viewKey], [key]: value } },
          })),
        reset: (viewKey) =>
          set((state) => {
            const mode = state.prefs[viewKey]?.mode
            const next = { ...state.prefs }
            if (mode) next[viewKey] = { mode }
            else delete next[viewKey]
            return { prefs: next }
          }),
      }),
      {
        name: 'moly-calendar-display',
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({ prefs: state.prefs }),
      },
    ),
  ),
)

/**
 * The resolved options for one view. Subscribes with a selector (not a
 * getState getter) so React Compiler re-renders on change.
 */
export function useCalendarDisplay(viewKey: string): CalendarDisplayPrefs {
  const stored = useCalendarDisplayStore((s) => s.prefs[viewKey])
  return useMemo(() => ({ ...defaultCalendarDisplay, ...stored }), [stored])
}

export default useCalendarDisplayStore
